<template>
  <div class="mc-loads-table">
    <div class="clearfix">
      <div class="mc-list-header">
        <div class="d-flex justify-content-between">
          <h4 class="card-title flex-grow-1">
            {{ $t('delivery.load') }}
            <McBadge
              v-if="
                hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update') &&
                (status === 'Diambil' || status === 'Terkirim')
              "
              :variant="isLoadAdjusted ? 'outlineWarning' : 'outlineSuccess'"
              :text="
                isLoadAdjusted
                  ? $t('delivery.loadsTable.badge.quantityAdjusted')
                  : $t('delivery.loadsTable.badge.asPlanned')
              "
              :icon="isLoadAdjusted ? 'icon-outline_alert-triangle' : 'icon-fill_checkmark-circle'"
              :iconVariant="isLoadAdjusted ? 'warning' : 'success'"
              size="medium"
              class="ms-2"
              type="button"
            />
          </h4>
          <div>
            <div
              v-if="($route.params.id && status === 'Diambil') || status === 'Terkirim'"
              class="mc-loads-table__toggle"
            >
              <McButton
                v-if="
                  hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update')
                "
                variant="secondary"
                icon="icon-fill_list"
                :text="$t('delivery.loadsTable.action.loadList')"
                :class="{ active: activeView === 'list' }"
                :disabled="items.length === 0"
                @click="toggleView('list')"
              />
              <McButton
                v-if="
                  hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update')
                "
                v-b-tooltip.hover="{
                  title: $t('delivery.loadsTable.tooltip.actualMatchesPlan'),
                  disabled: isLoadAdjusted,
                }"
                variant="secondary"
                icon="icon-outline_history"
                :text="$t('delivery.loadsTable.action.quantityChangeHistory')"
                :class="{ active: activeView === 'history' }"
                :disabled="!isLoadAdjusted"
                @click="toggleView('history')"
              />
            </div>
            <McButton
              v-if="$route.params.id && selectedIdsLength"
              variant="secondary-red"
              class="mx-2 ms-auto"
              icon="icon-outline_trash"
              :text="$t('global.delete')"
              @click="$refs['mc-modal-delete'].show()"
            />

            <McButton
              v-if="$route.params.id && status === 'Draf'"
              variant="primary"
              class="ms-auto"
              :text="$t('global.create')"
              icon="icon-fill_plus"
              :disabled="disabledGlobal"
              @click="handleAdd"
            />
          </div>
        </div>
      </div>
    </div>

    <b-card>
      <div v-if="!$route.params.id" class="d-flex justify-content-center">
        <div class="d-flex w-auto gap-3">
          <div>
            <img :src="illustrationPath" alt />
          </div>
          <div class="d-flex flex-column justify-content-center gap-1">
            <strong class="primary-disabled-text">{{ $t('delivery.notLoad') }}</strong>
            <p class="secondary-disabled-text">
              {{ $t('global.action.save') }}
              <span class="blue-disabled-text">{{ $t('delivery.infoDelivery') }} </span>
              {{ $t('delivery.first') }}
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <b-row align-h="end">
          <b-col md="8" xl="10" class="d-flex justify-content-between">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.selectedRows') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
            <McAlert
              v-if="status === 'Draf' && setBoxType && !canAddProduct"
              variant="warning"
              :html="$t('delivery.loadsTable.alert.noProductMatchedBoxType')"
              withIcon
              :withGap="false"
            />
            <McAlert
              v-if="invalidKeysLength && !selectedIdsLength"
              variant="error"
              :text="$t('delivery.loadsTable.alert.invalidRow')"
            >
              <template #prefix>
                <div class="mc-alert__custom_prefix">
                  <p>{{ invalidKeysLength }}</p>
                </div>
              </template>
            </McAlert>
            <template v-if="activeView !== 'history' && hasAccessAll(['tms-cold-chain:read'])">
              <div class="flex-1" />
              <div class="temperature-summary-container">
                <span class="__label">{{ $t('delivery.loadsTable.temperatureSummary') }}: </span>
                <span class="__value">{{ getTemperatureSummary }}</span>
                <i
                  v-b-tooltip.hover.bottomleft="
                    $t('delivery.loadsTable.temperatureSummaryTooltip')
                  "
                  class="icon-outline_info __tooltip-icon"
                />
              </div>
            </template>
          </b-col>
          <b-col v-if="activeView !== 'history'" md="4" xl="2">
            <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                ref="searchInput"
                :placeholder="`${$t('global.search')}...`"
                :disabled="disabledGlobal"
                :value="search"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button
                  class="clear-search-btn"
                  :disabled="disabledGlobal"
                  @click="handleClearSearch"
                >
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>

        <div
          v-if="
            ($route.params.id && status === 'Diambil') ||
            status === 'Terkirim' ||
            status === 'Sedang Transit'
          "
          class="mc-loads-table__sent"
        >
          <div class="mc-loads-table__box">
            <div v-show="activeView === 'list'" class="mc-loads-table__box-item">
              <div class="table-responsive border border-1 my-4">
                <table class="table b-table custom-table">
                  <thead>
                    <tr>
                      <th
                        v-for="field in getFirstRowHeaders()"
                        :key="field.key"
                        :rowspan="field.rowspan || 1"
                        :colspan="field.colspan || 1"
                        :class="field.thClass"
                        :style="field.thStyle"
                      >
                        {{ field.label }}
                      </th>
                    </tr>
                    <tr>
                      <th
                        v-for="field in getSecondRowHeaders()"
                        :key="field.key"
                        :style="field.thStyle"
                        :class="field.thClass"
                      >
                        {{ field.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="isBusy">
                      <td :colspan="getTotalColumns()" class="text-center">
                        <div class="__busy">
                          <b-spinner class="align-middle mb-2" />
                          <strong>{{ $t('global.text.loading') }}...</strong>
                        </div>
                      </td>
                    </tr>
                    <tr v-else-if="items.length === 0">
                      <td :colspan="getTotalColumns()" class="text-center my-4">
                        {{ $t('global.text.noData') }}
                      </td>
                    </tr>
                    <tr v-for="(item, rowIndex) in items" :key="rowIndex">
                      <td
                        v-for="field in getFlattenedFields()"
                        :key="field.key"
                        :style="field?.thStyle"
                      >
                        <template v-if="field.key === 'product.name'">
                          <div
                            v-b-tooltip.right="
                              item.product?.name?.length > 60 ? item.product.name : null
                            "
                            class="td__overflow-ellipsis-three-lines"
                          >
                            {{ item.product ? item.product.name : '-' }}
                          </div>
                          <br v-if="item.product && item.product.product_type" />
                          <small v-if="item.product && item.product.product_type">
                            {{ item.product.product_type.name }}
                          </small>
                        </template>
                        <template v-else-if="field.key === 'quantity'">
                          <div class="text-center">
                            <span>{{ `${item.quantity !== null ? formatNumber(item.quantity) : '-'} ` }}</span>
                            <span v-if="item.quantity !== null && item.uom">{{ item.uom }}</span>
                          </div>
                        </template>
                        <template v-else-if="field.key === 'total_weight'">
                          <div class="text-center">
                            <span>
                              {{
                                `${
                                  formatNumber(item.total_weight)
                                    ? formatNumber(item.total_weight)
                                    : '-'
                                } Kg`
                              }}
                            </span>
                            •
                            <span v-if="item.total_weight !== null && item.total_volume !== null">
                              {{
                                `${
                                  formatNumber(item.total_volume)
                                    ? formatNumber(item.total_volume)
                                    : '-'
                                } Cbm`
                              }}
                            </span>
                          </div>
                        </template>
                        <template v-else-if="field.key === 'quantity_picked'">
                          <div class="text-center">
                            <span :class="{ 'yellow-400': isAdjustedPicked }">
                              {{ item.quantity_picked !== null ? formatNumber(item.quantity_picked) : '-' }}
                            </span>
                            <span
                              v-if="item.quantity_picked !== null && item.uom"
                              :class="{ 'yellow-400': isAdjustedPicked }"
                            >
                              {{ item.uom }}
                            </span>
                          </div>
                        </template>
                        <template v-else-if="field.key === 'weight_picked'">
                          <div class="text-center">
                            <span :class="{ 'yellow-400': isAdjustedPicked }">
                              {{
                                `${
                                  formatNumber(item.weight_picked)
                                    ? formatNumber(item.weight_picked)
                                    : '-'
                                } Kg`
                              }}
                            </span>
                            •
                            <span
                              v-if="item.weight_picked !== null && item.volume_picked !== null"
                              :class="{ 'yellow-400': isAdjustedPicked }"
                            >
                              {{
                                `${
                                  formatNumber(item.volume_picked)
                                    ? formatNumber(item.volume_picked)
                                    : '-'
                                } Cbm`
                              }}
                            </span>
                          </div>
                        </template>

                        <template v-else-if="field.key === 'adjustment_reason_picked'">
                          <div
                            v-b-tooltip.right="
                              item.adjustment_reason_picked?.length > 60
                                ? item.adjustment_reason_picked
                                : null
                            "
                            class="td__overflow-ellipsis-three-lines text-center"
                          >
                            {{
                              item.adjustment_reason_picked ? item.adjustment_reason_picked : '-'
                            }}
                          </div>
                        </template>
                        <template v-else-if="field.key === 'quantity_delivered'">
                          <span :class="{ 'yellow-400': isAdjustedDelivered }">
                            {{ item.quantity_delivered !== null ? formatNumber(item.quantity_delivered) : '-' }}
                          </span>
                          <span
                            v-if="item.quantity_delivered !== null && item.uom"
                            :class="{ 'yellow-400': isAdjustedDelivered }"
                          >
                            {{ item.uom }}
                          </span>
                        </template>
                        <template v-else-if="field.key === 'weight_delivered'">
                          <div class="text-center">
                            <span :class="{ 'yellow-400': isAdjustedDelivered }">
                              {{
                                `${
                                  formatNumber(item.weight_delivered)
                                    ? formatNumber(item.weight_delivered)
                                    : '-'
                                } Kg`
                              }}
                            </span>
                            •
                            <span
                              v-if="
                                item.weight_delivered !== null && item.volume_delivered !== null
                              "
                              :class="{ 'yellow-400': isAdjustedDelivered }"
                            >
                              {{
                                `${
                                  formatNumber(item.volume_delivered)
                                    ? formatNumber(item.volume_delivered)
                                    : '-'
                                } Cbm`
                              }}
                            </span>
                          </div>
                        </template>
                        <template v-else-if="field.key === 'adjustment_reason_delivered'">
                          <div
                            v-b-tooltip.right="
                              item.adjustment_reason_delivered?.length > 60
                                ? item.adjustment_reason_delivered
                                : null
                            "
                            class="td__overflow-ellipsis-three-lines text-center"
                          >
                            {{
                              item.adjustment_reason_delivered
                                ? item.adjustment_reason_delivered
                                : '-'
                            }}
                          </div>
                        </template>
                        <template
                          v-else-if="
                            field.name === 'minTemperature' || field.name === 'maxTemperature'
                          "
                        >
                          {{ field?.formatter(item) }}
                        </template>
                        <template v-else-if="field.key === 'note'">
                          <div
                            v-b-tooltip.right="item.note?.length > 60 ? item.note : null"
                            class="td__overflow-ellipsis-three-lines"
                          >
                            {{ item.note ? item.note : '-' }}
                          </div>
                        </template>
                        <template v-else>
                          <span class="text-center">{{ getItemValue(item, field.key) }}</span>
                        </template>
                      </td>
                    </tr>
                    <tr v-if="items.length > 0" class="table-total-row">
                      <td colspan="2">
                        <span class="fw-bold">{{ $t('delivery.loadsTable.total') }}</span>
                      </td>

                      <td class="text-center">
                        <span class="fw-bold">{{
                          `${formatNumber(getTotalWeightPlanned())} Kg`
                        }}</span>
                        •
                        <span class="fw-bold">{{
                          `${formatNumber(getTotalVolumePlanned())} Cbm`
                        }}</span>
                      </td>

                      <td>&nbsp;</td>

                      <template
                        v-if="
                          hasAccessAll(
                            'delivery-load-adjustment:read',
                            'delivery-load-adjustment:update'
                          )
                        "
                      >
                        <td class="text-center">
                          <span class="fw-bold">{{
                            `${formatNumber(getTotalWeightPicked())} Kg`
                          }}</span>
                          •
                          <span class="fw-bold">{{
                            `${formatNumber(getTotalVolumePicked())} Cbm`
                          }}</span>
                        </td>
                        <template v-if="status === 'Terkirim'">
                          <td>&nbsp;</td>
                          <td>&nbsp;</td>
                        </template>
                        <td v-if="status === 'Terkirim'" class="text-center">
                          <span class="fw-bold">{{
                            `${formatNumber(getTotalWeightDelivered())} Kg`
                          }}</span>
                          •
                          <span class="fw-bold">{{
                            `${formatNumber(getTotalVolumeDelivered())} Cbm`
                          }}</span>
                        </td>
                        <td v-else>&nbsp;</td>
                        <td
                          :colspan="getTotalColumns() - getColumnIndexByKey('total_weight') - 1"
                        />
                      </template>
                      <template v-else>
                        <template v-if="hasAccessAll(['tms-cold-chain:read'])">
                          <td>&nbsp;</td>
                          <td>&nbsp;</td>
                        </template>
                        <td>&nbsp;</td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="d-flex flex-wrap flex-md-nowrap">
                <div class="mc-limit">
                  <div class="__limit">
                    <div>{{ $t('global.show') }}</div>
                    <b-form-select
                      class="form-select form-select-sm __limit--dropdown"
                      size="sm"
                      :disabled="disabledGlobal"
                      :options="limitOptions"
                      :value="limit"
                      @input="handleLimitChange"
                    />
                    <div>{{ $t('global.object.data').toLowerCase() }}.</div>
                  </div>

                  <div v-if="totalCount === 0" class="__info">
                    <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
                    <strong>{{ totalCount }}</strong> {{ $t('global.object.data').toLowerCase() }}.
                  </div>

                  <div v-else class="__info">
                    <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
                    {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
                    {{ $t('global.object.data').toLowerCase() }}.
                  </div>
                </div>
                <div class="mc-pagination-container">
                  <b-pagination
                    :disabled="disabledGlobal"
                    :per-page="limit"
                    :total-rows="totalCount"
                    :value="page"
                    @change="handlePageChange"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="
                hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update')
              "
              v-show="activeView === 'history'"
              class="mc-loads-table__box-item"
            >
              <TableHistory
                :deliveryId="$route.params.id"
                :isAdjustedPicked="isAdjustedPicked"
                :isAdjustedDelivered="isAdjustedDelivered"
                :status="status"
              />
            </div>
          </div>
        </div>

        <template v-else>
          <FormulateForm
            ref="formRef"
            v-model="formValues"
            name="editable-table-form"
            :errors="formulateFormErrors"
            @submit="handleSubmit"
            @failed-validation="handleFailedValidation"
          >
            <div class="table-responsive border border-1 mc-loads-table__editable-table">
              <b-table
                :busy="isBusy"
                :items="items"
                :fields="computedFields"
                :per-page="limit"
                fixed
                show-empty
                class="editable-table"
              >
                <template #table-busy>
                  <div class="__busy">
                    <b-spinner class="align-middle mb-2" />
                    <strong>{{ $t('global.text.loading') }}...</strong>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center my-4">
                    {{ $t('global.text.noData') }}
                  </div>
                </template>

                <template #head(checkbox)>
                  <div class="form-check" style="width: 10px">
                    <b-form-checkbox
                      v-model="selectedAll"
                      :disabled="disabledGlobal"
                      @change="handleSelectAll"
                    />
                  </div>
                </template>

                <template #head()="data">
                  <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                    {{ data.label }}
                    <template v-if="data.field.isSortable">
                      <div>
                        <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                          <i class="mc-icon icon-outline_chevron-up"></i>
                        </div>
                        <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                          <i class="mc-icon icon-outline_chevron-down"></i>
                        </div>
                      </div>
                    </template>
                  </div>
                </template>

                <template #cell(checkbox)="data">
                  <div class="form-check">
                    <b-form-checkbox
                      :checked="selectedIds[data.item.id]"
                      :disabled="disabledGlobal"
                      @change="(event) => handleSelect(event, data)"
                    />
                  </div>
                </template>

                <template #cell()="{ index, field, value, item }">
                  <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                    <FormulateInput
                      :ref="`${field.name}-ref`"
                      v-bind="{ ...field }"
                      :rowItem="item"
                      :label="null"
                      :name="field.name"
                      :type="field.type || 'text'"
                      :initialObject="
                        field.getInitialObject
                          ? field.name === 'product_id'
                            ? field.getInitialObject(productObj)
                            : field.getInitialObject(uomObj)
                          : undefined
                      "
                      :disabled="isSubmitting || (field.name === 'uom' && productObj.id === null)"
                      :help="field?.validation?.includes('required')"
                      class="p-0"
                      validation-behavior="live"
                      error-behavior="value"
                      size="sm"
                    />
                  </template>

                  <template v-else-if="field.isRedirectable">
                    <div class="fw-bold text-primary">
                      {{ value }}
                    </div>
                  </template>

                  <template v-else-if="!field.isEditable && isEditingRow == index">
                    <div>
                      {{ showOnlyValue(value, field) }}
                    </div>
                  </template>

                  <template v-else-if="field.name === 'product_id' || field.name === 'note'">
                    <div
                      v-b-tooltip.hover.right="value.length > 70 ? value : null"
                      class="td__overflow-ellipsis-three-lines"
                    >
                      {{ value }}
                    </div>
                  </template>

                  <template v-else-if="field.name === 'product_name_type'">
                    <div
                      v-b-tooltip.right="item.product?.name?.length > 60 ? item.product.name : null"
                      class="td__overflow-ellipsis-three-lines"
                    >
                      {{ item.product ? item.product.name : '-' }}
                    </div>
                    <br v-if="item.product && item.product.product_type" />
                    <small v-if="item.product && item.product.product_type">
                      {{ item.product.product_type.name }}
                    </small>
                  </template>

                  <template v-else-if="field.name === 'product_type'">
                    <div
                      v-b-tooltip.hover.right="value && value.length > 70 ? value : null"
                      class="td__overflow-ellipsis-three-lines"
                    >
                      {{
                        value ||
                        (item.product && item.product.product_type
                          ? item.product.product_type.name
                          : '-')
                      }}
                    </div>
                  </template>

                  <template v-else-if="field?.formatter">
                    {{ field?.formatter(item[field?.key], item) }}
                  </template>

                  <template v-else>
                    <div
                      v-b-tooltip.hover.right="value.length > 10 ? value : null"
                      class="td__overflow-ellipsis"
                      :class="
                        invalidItems[item.id] && invalidItems[item.id][field.name]
                          ? 'td__invalid-data'
                          : undefined
                      "
                    >
                      {{ transformCellValue(value, field) }}
                    </div>
                  </template>
                </template>

                <template #cell(action)="data">
                  <div v-if="isEditingRow == data.index">
                    <b-button variant="outline" class="__accept-button btn" type="submit">
                      <i class="mc-icon icon-fill_checkmark"></i>
                    </b-button>

                    <b-button
                      variant="outline"
                      class="__cancel-button btn"
                      @click="handleEditCancel(data)"
                    >
                      <i class="mc-icon icon-outline_close"></i>
                    </b-button>
                  </div>

                  <b-button
                    v-else
                    variant="outline"
                    :class="{
                      '__edit-button btn': true,
                      __disabled: isEditingRow !== null || isBusy,
                    }"
                    @click="handleEditStart(data)"
                  >
                    <i class="mc-icon icon-outline_edit"></i>
                  </b-button>
                </template>

                <template #custom-foot>
                  <b-tr>
                    <b-td :colspan="getTotalColumnsBeforeWeight()">
                      {{ $t('delivery.loadsTable.total') }}
                    </b-td>
                    <template v-if="['Dikonfirmasi', 'Dijadwalkan', 'Dijalankan'].includes(status)">
                      <b-td class="text-center">
                        {{ `${formatNumber(computedTotalWeights)} Kg` }}
                        •
                        {{ `${formatNumber(computedTotalVolumes)} Cbm` }}
                      </b-td>
                    </template>
                    <template v-else>
                      <b-td>
                        {{ `${formatNumber(computedTotalWeights)} Kg` }}
                      </b-td>
                      <b-td>
                        {{ `${formatNumber(computedTotalVolumes)} Cbm` }}
                      </b-td>
                    </template>
                    <b-td
                      v-if="getTotalColumnsAfterWeight() > 0"
                      :colspan="getTotalColumnsAfterWeight()"
                    />
                  </b-tr>
                </template>
                <template v-for="(index, name) in $scopedSlots" #[name]="data">
                  <slot :name="name" v-bind="data"></slot>
                </template>
              </b-table>
            </div>
          </FormulateForm>
          <div class="d-flex flex-wrap flex-md-nowrap">
            <div class="mc-limit">
              <div class="__limit">
                <div>{{ $t('global.show') }}</div>
                <b-form-select
                  class="form-select form-select-sm __limit--dropdown"
                  size="sm"
                  :disabled="disabledGlobal"
                  :options="limitOptions"
                  :value="limit"
                  @input="handleLimitChange"
                />
                <div>{{ $t('global.object.data').toLowerCase() }}.</div>
              </div>

              <div v-if="totalCount === 0" class="__info">
                <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
                <strong>{{ totalCount }}</strong> {{ $t('global.object.data').toLowerCase() }}.
              </div>

              <div v-else class="__info">
                <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
                {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
                {{ $t('global.object.data').toLowerCase() }}.
              </div>
            </div>
            <div class="mc-pagination-container">
              <b-pagination
                :disabled="disabledGlobal"
                :per-page="limit"
                :total-rows="totalCount"
                :value="page"
                @change="handlePageChange"
              />
            </div>
          </div>
        </template>
      </div>

      <b-modal
        id="mc-modal-delete"
        ref="mc-modal-delete"
        class="mc-modal"
        :title="$t('global.action.delete')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { sum, substract, multiply } from '@/helpers/calculate'
import isEmpty from 'lodash/isEmpty'
import { formatNumber, formatNumberTruncate } from '@/helpers/format-number.js'

import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McBadge from '@/components/McBadge'

import { getLoadFieldsDefault, getLoadFieldsByStatus } from '../../lib/fields/loadFields'
import getLoadFieldsSend from '../../lib/fields/loadFieldsSend'
import TableHistory from './TableHistory'

import * as Uom from '@/services/rest-api/lib/product/uom'

import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
    McBadge,
    TableHistory,
  },
  props: {
    apiClient: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: '',
    },
    totalWeights: {
      type: Number,
      default: 0,
    },
    totalVolumes: {
      type: Number,
      default: 0,
    },
    loadMinTemp: {
      type: Number,
      default: null,
    },
    loadMaxTemp: {
      type: Number,
      default: null,
    },
    limitOptions: {
      type: Array,
      default: () => [5, 10, 25, 50, 100],
    },
    invalidFields: {
      type: Array,
      default: () => [],
    },
    isLoadAdjusted: {
      type: Boolean,
      default: false,
    },
    setBoxType: {
      type: Boolean,
      default: false,
    },
    boxTypeFullObject: {
      type: Object,
      default: () => ({}),
    },
    canAddProduct: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:totalWeights', 'update:totalVolumes', 'refetch-show-delivery'],
  data() {
    return {
      isBusy: true,
      isSubmitting: false,
      limit: this.limitOptions[0] ?? 10,
      loadFields: [],
      loadFieldsSend: [],
      page: 1,
      totalCount: 0,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},

      loadsData: {},
      showResponse: {},
      productObj: {},
      uomObj: {},
      invalidItems: {},
      formulateFormErrors: {},
      activeView: 'list',
      isAdjustedDelivered: false,
      tooltips: {},
      isAdjustedPickedState: null,
    }
  },
  computed: {
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy || !this.canAddProduct
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      const t = (key, params) => this.$t(key, params)

      if (this.status == 'Draf') {
        return [
          {
            key: 'checkbox',
            label: '',
            thStyle: 'width: 25px',
          },
          ...this.loadFields,
          {
            key: 'action',
            label: '',
            thClass: 'mc-action-col-head',
            tdClass: 'mc-action-cell',
          },
        ]
      } else if (['Dikonfirmasi', 'Dijadwalkan', 'Dijalankan'].includes(this.status)) {
        return [...getLoadFieldsByStatus(t)[this.status]]
      } else if (this.status == 'Diambil' || this.status == 'Terkirim') {
        return [...this.loadFieldsSend]
      } else {
        return [...this.loadFields]
      }
    },
    isAdjustedPicked: {
      get() {
        if (this.isAdjustedPickedState !== null && this.isAdjustedPickedState !== undefined) {
          return this.isAdjustedPickedState
        }

        return this.items.some(
          (item) => item.quantity !== item.quantity_picked && item.quantity_picked !== null
        )
      },
      set(val) {
        this.isAdjustedPickedState = val
      },
    },
    computedTotalWeights() {
      let totalWeights = this.totalWeights

      // if it's in editing mode
      if (this.isEditingRow != null) {
        if (this.totalCount > 0) {
          totalWeights = substract(totalWeights, this.showResponse.total_weight)
        }

        const item = this.items[this.isEditingRow]

        totalWeights = sum(totalWeights || 0, item.total_weight)
      }

      return totalWeights
    },
    computedTotalVolumes() {
      let totalVolumes = this.totalVolumes

      // if it's in editing mode
      if (this.isEditingRow != null) {
        if (this.totalCount > 0) {
          totalVolumes = substract(totalVolumes, this.showResponse.total_volume)
        }

        const item = this.items[this.isEditingRow]

        totalVolumes = sum(totalVolumes || 0, item.total_volume)
      }

      return totalVolumes
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },

    getTemperatureSummary() {
      if (this.loadMinTemp === null && this.loadMaxTemp === null) return `-°C`

      if (this.loadMaxTemp === null) return `≥ ${this.loadMinTemp}°C`

      if (this.loadMinTemp === null) return `≤ ${this.loadMaxTemp}°C`

      return `${this.loadMinTemp}°C ~ ${this.loadMaxTemp}°C`
    },
  },
  created() {
    this.refreshTranslatedFields()
  },
  watch: {
    setBoxType() {
      this.refreshLoadFields()
    },
    'boxTypeFullObject.id'() {
      this.refreshLoadFields()
    },
    async formValues() {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(this.formValues, this.loadsData)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.product_id': async function (newValue, oldValue) {
      // If user unselects product
      if (newValue === undefined) {
        // Reset fields pagination info
        if (this.$refs['uom-ref']?.$children) {
          this.$refs['uom-ref'].$children[0].ResetProperties()
        }

        this.$set(this.formValues, 'quantity', null)
        this.$set(this.formValues, 'uom', null)
        this.$set(this.formValues, 'note', null)

        return
      }

      // If value doesn't change
      if (newValue === oldValue) {
        return
      }

      // If value changes
      // change uom options getter id to product id
      const uomFieldIdx = this.loadFields.findIndex((field) => field.name === 'uom')
      if (uomFieldIdx === -1) return
      this.$set(this.loadFields[uomFieldIdx], 'optionsGetter', Uom.get(this.formValues.product_id))

      // Reset fields pagination info
      this.$refs['uom-ref']?.$children[0].ResetProperties()

      const productObjSelected = this.$refs['product_id-ref']?.$children[0].fullObject

      // If productObjSelected is empty because show, the total_weight and
      // total_volume not computed again in here
      if (isEmpty(productObjSelected)) {
        return
      }

      // assign productObj to be props as initialObject
      this.productObj = productObjSelected
      const quantity = this.formValues.quantity ? Number(this.formValues.quantity) : null

      // Pre-fill loads weight and volume when selecting product
      this.$set(this.items[this.isEditingRow], 'product', this.productObj)
      this.$set(
        this.items[this.isEditingRow],
        'total_weight',
        quantity ? multiply(this.productObj.weight ?? 0, quantity) : this.productObj.weight
      )
      this.$set(
        this.items[this.isEditingRow],
        'total_volume',
        quantity ? multiply(this.productObj.volume ?? 0, quantity) : this.productObj.volume
      )

      // set product temperature
      this.$set(
        this.items[this.isEditingRow],
        'min_temperature',
        this.productObj.product_type.product_category.min_temperature
      )
      this.$set(
        this.items[this.isEditingRow],
        'max_temperature',
        this.productObj.product_type.product_category.max_temperature
      )
      this.$set(
        this.items[this.isEditingRow],
        'box_type',
        this.productObj.product_type?.product_category?.box_type
      )

      // Pre-fill selected uom with base-uom
      if (this.productObj?.uom) {
        this.uomObj = {
          id: '',
          name: this.productObj.uom,
          base_uom: this.productObj.uom,
          weight: this.productObj.weight,
          volume: this.productObj.volume,
        }

        // Setting selected value of uom
        this.formValues.uom = this.productObj.uom
      }
    },
    'formValues.quantity': function (newValue, oldValue) {
      if (this.isEditingRow === null) return

      // Clear quantity value
      if (newValue == '' && !newValue) return

      // Same value
      if (newValue == oldValue) return

      // If user input valid quantity
      this.$set(
        this.items[this.isEditingRow],
        'total_weight',
        multiply(this.uomObj?.weight ?? 0, newValue)
      )
      this.$set(
        this.items[this.isEditingRow],
        'total_volume',
        multiply(this.uomObj?.volume ?? 0, newValue)
      )
    },
    'formValues.uom': function (newValue, oldValue) {
      // Clear quantity value
      if (newValue == undefined) return

      // Same value
      if (newValue == oldValue) return

      // Get value from select field after change
      const uomFields = this.$refs['uom-ref']?.$children[0]
      const _uomObj = uomFields?.options.find((item) => item.name === newValue)

      // If uom items found in options, set the state to it
      // else, use base uom
      if (_uomObj !== undefined) this.uomObj = { ..._uomObj }

      if (this.uomObj?.name !== this.productObj.uom) {
        // if selected uomObj doesn't have weight/volume
        // follow base uom
        if (Number(this.uomObj.weight) === 0) this.uomObj.weight = this.productObj.weight
        if (Number(this.uomObj.volume) === 0) this.uomObj.volume = this.productObj.volume
      }

      const quantity = this.formValues.quantity ? Number(this.formValues.quantity) : 0

      this.$set(
        this.items[this.isEditingRow],
        'total_weight',
        multiply(this.uomObj.weight ?? 0, quantity)
      )
      this.$set(
        this.items[this.isEditingRow],
        'total_volume',
        multiply(this.uomObj.volume ?? 0, quantity)
      )
    },
  },
  async mounted() {
    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    refreshTranslatedFields() {
      const t = (key, params) => this.$t(key, params)
      this.loadFieldsSend = getLoadFieldsSend(t)
      this.refreshLoadFields()
    },
    refreshLoadFields() {
      const t = (key, params) => this.$t(key, params)
      let fields = this.filterFieldsByScopes(getLoadFieldsDefault(t))

      if (this.setBoxType && this.boxTypeFullObject?.id) {
        fields = fields.map((field) => {
          if (field.name === 'product_id') {
            const newField = { ...field }
            const originalListGetter = field.listGetter
            newField.listGetter = (params) => {
              const filters = params?.filters || []
              const newFilters = Array.isArray(filters) ? [...filters] : [filters]
              newFilters.push(
                `product_type.product_category.box_type.id eq ${this.boxTypeFullObject.id}`
              )
              return originalListGetter({ ...params, filters: newFilters })
            }
            return newField
          }
          return field
        })
      }

      const uomFieldIdx = fields.findIndex((field) => field.name === 'uom')
      if (uomFieldIdx !== -1 && this.formValues.product_id) {
        fields[uomFieldIdx] = {
          ...fields[uomFieldIdx],
          optionsGetter: Uom.get(this.formValues.product_id),
        }
      }

      this.loadFields = fields
    },
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    formatNumber(value) {
      return formatNumber(value, 3) || '-'
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value, field) {
      if (field.name === 'product_id' && this.productObj.id === null) {
        return this.productObj.name || `- ${field.afterText ?? ''}`
      }

      if (
        field.name === 'product_type' &&
        this.productObj.product_type &&
        this.productObj.product_type.id === null
      ) {
        return this.productObj.product_type.name || `- ${field.afterText ?? ''}`
      }

      if (field.name === 'uom' && this.productObj.id === null) {
        return this.formValues.uom || value || `- ${field.afterText ?? ''}`
      }

      if (!this.formValues.product_id || !value) return `- ${field.afterText ?? ''}`

      let valueShown = value

      if (['total_weight', 'total_volume'].includes(field.name)) {
        valueShown = formatNumber(value, 3)
      }

      if (['min_temperature', 'max_temperature'].includes(field.name)) {
        valueShown = this.productObj.product_type.product_category[field.name] ?? '-'
      }

      return `${valueShown} ${field.afterText ?? ''}`
    },
    transformCellValue(value, field) {
      if (value === false) return `- ${field.afterText ?? ''}`
      if (field.name === 'quantity') return formatNumberTruncate(value, 3)
      if (typeof value === 'number') return `${formatNumber(value, 3)} ${field.afterText ?? ''}`
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            const isQuantity = [key].includes('quantity')
            if (isQuantity){
              return {[key]: formatNumberTruncate(value, 3, false, true)} 
            }
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    AssignInvalidErrors() {
      if (!this.invalidFields.length) return
      const indexObject = {}

      // format the invalidFields from array to object with id as the key
      for (const load of this.invalidFields) {
        const loadObj = {}

        for (const field in load) {
          if (field === 'id') continue
          loadObj[field] = ['Data telah dihapus dari sistem.']
        }

        indexObject[load.id] = loadObj
      }

      // assign to invalidItems as state that could be used to show error
      this.invalidItems = indexObject
    },
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (!res) return

      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count

      if (res.data.is_adjusted_picked !== undefined) {
        this.isAdjustedPicked = res.data.is_adjusted_picked
      } else if (res.metadata && res.metadata.is_adjusted_picked !== undefined) {
        this.isAdjustedPicked = res.metadata.is_adjusted_picked
      } else if (res.is_adjusted_picked !== undefined) {
        this.isAdjustedPicked = res.is_adjusted_picked
      } else {
        const hasAdjustment = this.items.some(
          (item) =>
            item.quantity !== item.quantity_picked &&
            item.quantity_picked !== null &&
            item.quantity_picked !== undefined
        )
        this.isAdjustedPicked = hasAdjustment
      }

      if (res.data.is_adjusted_delivered !== undefined) {
        this.isAdjustedDelivered = res.data.is_adjusted_delivered
      } else if (res.metadata && res.metadata.is_adjusted_delivered !== undefined) {
        this.isAdjustedDelivered = res.metadata.is_adjusted_delivered
      } else if (res.is_adjusted_delivered !== undefined) {
        this.isAdjustedDelivered = res.is_adjusted_delivered
      } else {
        const hasDeliveredAdjustment = this.items.some(
          (item) =>
            item.quantity !== item.quantity_delivered &&
            item.quantity_delivered !== null &&
            item.quantity_delivered !== undefined
        )
        this.isAdjustedDelivered = hasDeliveredAdjustment
      }

      this.isBusy = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.search = ''
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(data.item.id)
      if (!refetchedData) return

      const loadData = refetchedData.data

      // assign formValues with response show data
      this.loadFields.forEach((field) => {
        if (!field.isEditable) return
        let value

        if (field.name === 'product_id') {
          value = loadData.product.id !== null ? loadData.product.id : loadData.product.name
        } else {
          value = loadData[field.name]
        }

        this.$set(this.formValues, field.name, value)
      })

      // assign productObj with product object in response show data
      this.productObj = {
        ...loadData.product,
        product_type: {
          ...loadData.product_type,
          product_category: {
            min_temperature: loadData.min_temperature,
            max_temperature: loadData.max_temperature,
          },
        },
      }

      // assign uomObj and adjust weight/volume
      this.uomObj = {
        id: '',
        name: loadData.uom,
        weight: loadData.total_weight / loadData.quantity,
        volume: loadData.total_volume / loadData.quantity,
      }

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return loadData
        return item
      })

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }
      this.loadsData = { ...this.formValues }

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.id]) {
        this.formulateFormErrors = { ...this.invalidItems[loadData.id] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}
      this.productObj = {}
      this.uomObj = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      const refetchedData = await this.apiClient.show(createdData.data.id)
      if (!refetchedData) return false

      this.ids.push(refetchedData.data.id)
      this.totalCount += 1

      this.$mcToast.success('Data berhasil ditambahkan')

      this.$emit('refetch-show-delivery')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, this.loadsData)
      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      const updatedData = await this.apiClient.update(oldData.id, patchData)
      if (!updatedData) return false

      const refetchedData = await this.apiClient.show(oldData.id)
      if (!refetchedData) return false

      if (this.invalidItems[oldData.id]) {
        this.$delete(this.invalidItems, oldData.id)
        this.formulateFormErrors = {}
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')

      this.$emit('refetch-show-delivery')

      return {
        ...refetchedData.data,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.loadFields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.$emit('update:totalWeights', this.computedTotalWeights)
      this.$emit('update:totalVolumes', this.computedTotalVolumes)

      this.productObj = {}
      this.uomObj = {}
      this.showResponse = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }
      await Promise.all(promises)
      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })

      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')

      this.$emit('refetch-show-delivery')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
    toggleView(view) {
      this.activeView = view
    },

    getFirstRowHeaders() {
      return this.loadFieldsSend
        .filter((field) => !field.onlyForStatus || field.onlyForStatus.includes(this.status))
        .filter((field) => field.rowspan === 2 || field.isParentHeader)
    },

    getSecondRowHeaders() {
      return this.loadFieldsSend
        .filter((field) => !field.onlyForStatus || field.onlyForStatus.includes(this.status))
        .filter((field) => field.isChildHeader)
    },

    getTotalColumns() {
      return this.loadFieldsSend.filter((field) => field.rowspan === 2 || field.isChildHeader)
        .length
    },

    getFlattenedFields() {
      return this.loadFieldsSend
        .filter((field) => !field.onlyForStatus || field.onlyForStatus.includes(this.status))
        .filter((field) => field.rowspan === 2 || field.isChildHeader)
    },

    getItemValue(item, key) {
      if (!key.includes('.')) {
        return item[key]
      }

      const keys = key.split('.')
      let value = item

      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k]
        } else {
          return ''
        }
      }

      return value
    },

    getColumnIndexByKey(key) {
      const fields = this.getFlattenedFields()
      return fields.findIndex((field) => field.key === key)
    },

    getTotalColumnsBeforeWeight() {
      return this.computedFields.findIndex((field) => field.key === 'total_weight')
    },

    getTotalColumnsAfterWeight() {
      const weightIndex = this.computedFields.findIndex((field) => field.key === 'total_weight')

      const totalColumns = this.computedFields.length

      const weightColumns = ['Dikonfirmasi', 'Dijadwalkan', 'Dijalankan'].includes(this.status)
        ? 1
        : 2

      return totalColumns - weightIndex - weightColumns
    },

    getTotalWeightPlanned() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.total_weight) || 0)
      }, 0)
    },

    getTotalVolumePlanned() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.total_volume) || 0)
      }, 0)
    },

    getTotalWeightPicked() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.weight_picked) || 0)
      }, 0)
    },

    getTotalVolumePicked() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.volume_picked) || 0)
      }, 0)
    },

    getTotalWeightDelivered() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.weight_delivered) || 0)
      }, 0)
    },

    getTotalVolumeDelivered() {
      return this.items.reduce((total, item) => {
        return total + (Number(item.volume_delivered) || 0)
      }, 0)
    },
    filterFieldsByScopes(fields) {
      return fields
        .filter((field) => {
          // Only show fields user has access to
          if (field?.isShow === false) {
            return false
          }
          if (field?.scopes) {
            return this.hasAccessAll(field.scopes)
          }
          return true
        })
        .map((field) => {
          // Also filter nested tableFields
          if (field.tableFields) {
            return {
              ...field,
              tableFields: this.filterFieldsByScopes(field.tableFields),
            }
          }
          return field
        })
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
